import React, {RefObject, useEffect, useRef, useState} from 'react'
import styled from 'styled-components'

import {useIntersectionObserver} from '@miro-site/core'

import {IMAGE_FORMATS} from './imageFormats'

const AbsoluteContainer = styled.div`
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  position: absolute;
`

const RelativeContainer = styled.div`
  position: relative;
`

const Image = styled.img<{visible: boolean; lowRes?: boolean}>`
  width: 100%;
  height: auto;
  z-index: ${({visible}) => (visible ? '1' : '0')};
  opacity: ${({visible}) => (visible ? '1' : '0')};
  transition: opacity ${({theme}) => theme.animation.transitionDuration} ease-in-out;
  filter: ${({lowRes}) => lowRes && 'blur(6px)'};
`

const AbsoluteImage = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

export interface LazyImageType {
  '*'?: string
  svg?: string
  png?: string
  jpg?: string
  webp?: string
}

type ImageDimensions = {
  width?: number
  height?: number
}

export interface LazyImageProps extends ImageDimensions {
  placeholder?: LazyImageType
  original: LazyImageType
  className?: string
  lazyload?: boolean
  detectIntersection?: boolean
  rootRef?: RefObject<HTMLElement>
  name: string
}

export interface PictureProps extends ImageDimensions {
  pictures: LazyImageType
  name: string
  loaded: boolean
  onLoad?: () => void
  lowRes?: boolean
  lazyload?: boolean
  detectIntersection?: boolean
  rootRef?: RefObject<HTMLElement>
}

function processLazyload(element: Element): void {
  const sources = element.querySelectorAll<HTMLSourceElement>('source')
  const images = element.querySelectorAll<HTMLImageElement>('img')

  sources.forEach(s => {
    if (s.dataset.srcset) {
      s.srcset = s.dataset.srcset
    }
  })

  images.forEach(i => {
    if (!i.src && i.dataset.src) {
      i.src = i.dataset.src
    }
  })
}

const Picture = ({
  pictures = {},
  name,
  loaded,
  onLoad,
  lowRes,
  lazyload = true,
  detectIntersection = true,
  rootRef,
  width,
  height,
}: PictureProps) => {
  const container = useIntersectionObserver(rootRef, {
    rootMargin: '300px 0px 300px 0px',
    freezeOnceVisible: true,
  })

  const isVisible = !!container?.isIntersecting

  useEffect(() => {
    if (!lazyload || !rootRef?.current) {
      return
    }

    const isIntersectionObserverSupported =
      'IntersectionObserver' in window && 'IntersectionObserverEntry' in window

    if (!detectIntersection || !isIntersectionObserverSupported || isVisible) {
      processLazyload(rootRef.current)
    }
  }, [isVisible, lazyload, rootRef, processLazyload, detectIntersection])

  const ImageComponent = width && height ? AbsoluteImage : Image

  const original = ['jpg', 'png', 'svg', '*'].find(t => !!pictures[t])

  return (
    <picture>
      {pictures.webp && (
        <source
          data-srcset={lazyload ? pictures.webp : undefined}
          srcSet={!lazyload ? pictures.webp : undefined}
          type={IMAGE_FORMATS.webp}
        />
      )}
      {original && (
        <>
          <source
            data-srcset={lazyload ? pictures[original] : undefined}
            srcSet={!lazyload ? pictures[original] : undefined}
            type={IMAGE_FORMATS[original]}
          />
          <ImageComponent
            onLoad={() => {
              document.dispatchEvent(new Event('lazyloaded'))

              if (onLoad) {
                onLoad()
              }
            }}
            data-src={lazyload ? pictures[original] : undefined}
            src={!lazyload ? pictures[original] : undefined}
            width={width}
            height={height}
            alt={name}
            visible={loaded}
            lowRes={lowRes}
          />
        </>
      )}
    </picture>
  )
}

const LazyImage: React.FC<LazyImageProps> = ({
  placeholder,
  original,
  name,
  className,
  lazyload,
  detectIntersection,
  rootRef,
  width,
  height,
}) => {
  const [loaded, setLoaded] = useState(false)

  const WrapperComponent = width && height ? AbsoluteContainer : RelativeContainer

  const [containerRef, setContainerRef] = useState(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (rootRef?.current) {
      setContainerRef(rootRef)
      return
    }

    if (wrapperRef?.current) {
      setContainerRef(wrapperRef)
      return
    }
  }, [rootRef, wrapperRef])

  return (
    <WrapperComponent className={className} ref={wrapperRef}>
      {placeholder ? (
        <>
          <Picture
            rootRef={containerRef}
            pictures={original}
            name={name}
            lazyload={lazyload}
            detectIntersection={detectIntersection}
            loaded={loaded}
            onLoad={() => setLoaded(true)}
            width={width}
            height={height}
          />
          <Picture
            rootRef={containerRef}
            pictures={placeholder}
            width={width}
            height={height}
            lowRes
            lazyload={false}
            detectIntersection={detectIntersection}
            name={name}
            loaded={!loaded}
          />
        </>
      ) : (
        <Picture
          rootRef={containerRef}
          pictures={original}
          name={name}
          lazyload={lazyload}
          detectIntersection={detectIntersection}
          loaded={loaded}
          onLoad={() => setLoaded(true)}
          width={width}
          height={height}
        />
      )}
    </WrapperComponent>
  )
}

export default LazyImage
